import { getCurrentProjectData } from '../../../../../../UILibrary';

type DigitalSamplingAnalyticsEvent =
  | 'digital_sampling_request_a_quote_cta_click'
  | 'digital_sampling_request_a_quote_drawer_close'
  | 'digital_sampling_request_a_quote_drawer_project_selected'
  | 'digital_sampling_request_a_quote_drawer_purchasing_quantity'
  | 'digital_sampling_request_a_quote_drawer_learn_more_cta_click'
  | 'digital_sampling_request_a_quote_complete'
  | 'digital_sampling_request_a_quote_go_to_inbox_click'
  | 'digital_sampling_file_download_complete';

interface DigitalSamplingAnalyticsEventPayload {
  event?: DigitalSamplingAnalyticsEvent;
  location?: 'digital_sampling';
  event_location?: 'Product detail page' | 'Digital sampling download history';
  event_item_list_id?:
    | 'digital_sampling_pdp'
    | 'digital_sampling_download_history';
  event_item_list_name?:
    | 'digital sampling PDP'
    | 'digital sampling download history';
  event_item_type?: 'digital';
  event_project_name?: string;
  event_project_id?: string;
  event_item_id?: string;
  event_item_name?: string;
  event_item_brand?: string;
  event_item_category?: string;
  event_item_category2?: string;
  event_item_category3?: string;
  event_item_variant?: string;
  event_unit_type?: string; // 'units' | 'sq ft' | 'linear ft' | 'gallons' | 'yards';
  event_unit_input?: string;
  downloaded_item_count?: number;
}

export interface RequestQuoteAnalyticsEventParameters {
  projectId?: string;
  projectName?: string;
  quantity?: string;
  unitType?: string;
}

const createBaseEvent = (
  eventName: DigitalSamplingAnalyticsEventPayload['event'],
  eventOptions: RequestQuoteAnalyticsEventParameters = {},
) => {
  const productName = window.productAttributeJson?.name?.value;
  const productManufacturer = window.productAttributeJson?.manufacturer?.value;
  const productMbSku = window.productAttributeJson?.sku?.value;
  const taxonomies =
    window.productAttributeJson?.taxonomy_us?.value?.split('.') || [];
  const currentProjectData = getCurrentProjectData();
  const { projectId, projectName, quantity, unitType } = eventOptions;

  const event: DigitalSamplingAnalyticsEventPayload = {
    event: eventName,
    location: 'digital_sampling',
    event_location: 'Product detail page',
    event_item_list_id: 'digital_sampling_pdp',
    event_item_list_name: 'digital sampling PDP',
    event_item_type: 'digital',
    event_project_name: currentProjectData?.projectName,
    event_project_id: currentProjectData?.projectId,
    event_item_id: productMbSku,
    event_item_name: productName,
    event_item_brand: productManufacturer,
    event_item_category: taxonomies?.[0],
    event_item_category2: taxonomies?.[1],
    event_item_category3: taxonomies?.[2],
  };
  if (projectId) {
    event.event_project_id = projectId;
  }
  if (projectName) {
    event.event_project_name = projectName;
  }
  if (quantity) {
    event.event_unit_input = quantity;
  }
  if (unitType) {
    event.event_unit_type = unitType;
  }
  return event;
};

export const requestQuoteAnalyticsEvents = {
  requestQuoteCtaClicked: () => {
    const event = createBaseEvent('digital_sampling_request_a_quote_cta_click');
    dataLayer.push(event);
  },
  drawerClosed: (eventOptions: RequestQuoteAnalyticsEventParameters) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_drawer_close',
      eventOptions,
    );
    dataLayer.push(event);
  },
  projectUpdated: (eventOptions: RequestQuoteAnalyticsEventParameters) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_drawer_project_selected',
      eventOptions,
    );
    dataLayer.push(event);
  },
  purchaseQuantityUpdated: (
    eventOptions: RequestQuoteAnalyticsEventParameters,
  ) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_drawer_purchasing_quantity',
      eventOptions,
    );
    dataLayer.push(event);
  },
  learnMoreCtaClicked: (eventOptions: RequestQuoteAnalyticsEventParameters) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_drawer_learn_more_cta_click',
      eventOptions,
    );
    dataLayer.push(event);
  },
  requestQuoteCompleted: (
    eventOptions: RequestQuoteAnalyticsEventParameters,
  ) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_complete',
      eventOptions,
    );
    dataLayer.push(event);
  },
  goToInboxClicked: (eventOptions: RequestQuoteAnalyticsEventParameters) => {
    const event = createBaseEvent(
      'digital_sampling_request_a_quote_go_to_inbox_click',
      eventOptions,
    );
    dataLayer.push(event);
  },
};

export const previewFilesAnalyticsEvents = {
  fileDownloaded: (
    event: DigitalSamplingAnalyticsEventPayload,
    calledFrom: 'digital_sampling_pdp' | 'digital_sampling_download_history',
  ) => {
    let event_location, event_item_list_id, event_item_list_name;
    if (calledFrom === 'digital_sampling_pdp') {
      event_location = 'Product detail page';
      event_item_list_id = 'digital_sampling_pdp';
      event_item_list_name = 'digital sampling PDP';
    } else {
      event_location = 'Digital sampling download history';
      event_item_list_id = 'digital_sampling_download_history';
      event_item_list_name = 'digital sampling download history';
    }

    dataLayer.push({
      event: 'digital_sampling_file_download_complete',
      location: 'digital_sampling',
      event_item_type: 'digital',
      event_location,
      event_item_list_id,
      event_item_list_name,
      downloaded_item_count: event.downloaded_item_count,
      event_project_name: event.event_project_name,
      event_project_id: event.event_project_id,
      event_item_id: event.event_item_id, // mb sku
      event_item_name: event.event_item_name, // product name
      event_item_brand: event.event_item_brand, // product manufacturer
      event_item_category: event.event_item_category, // taxonomy_us or equivalent
      event_item_category2: event.event_item_category2, // taxonomy_us or equivalent
      event_item_category3: event.event_item_category3, // taxonomy_us or equivalent
      event_item_variant: event.event_item_variant, // colorway
    });
  },
};
